import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'

/**
 * Reorders a shape list for **DOM paint only** so each kind-7 group shell is drawn immediately
 * before its first direct child. Document z-order (array order) stays unchanged elsewhere; this
 * keeps the group's hover fill under descendant shape fills without moving the group in history.
 */
export function orderShapesWithGroupShellsBeforeDirectChildren(shapes: ShapeData[]): ShapeData[] {
  if (!shapes.some(isGroupShape)) {
    return shapes
  }

  const initialIndex = new Map(shapes.map((s, i) => [s.id, i]))
  // Ascending: relocate inner (earlier-in-list) groups before outer groups so nested shells stack
  // correctly after each splice.
  const groupsByInitialAsc = shapes
    .filter(isGroupShape)
    .sort((a, b) => (initialIndex.get(a.id) ?? 0) - (initialIndex.get(b.id) ?? 0))

  const order = [...shapes]

  for (const g of groupsByInitialAsc) {
    let minChildIdx = -1

    for (let idx = 0; idx < order.length; idx += 1) {
      if (order[idx].parentGroupId === g.id) {
        minChildIdx = minChildIdx < 0 ? idx : Math.min(minChildIdx, idx)
      }
    }

    if (minChildIdx < 0) {
      continue
    }

    const gIdx = order.findIndex((s) => s.id === g.id)

    if (gIdx < 0 || gIdx < minChildIdx) {
      continue
    }

    const [removed] = order.splice(gIdx, 1)

    order.splice(minChildIdx, 0, removed)
  }

  return order
}
